import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  DeleteProposalAttachmentInput,
  DeleteProposalAttachmentMutation,
} from '@/gql/graphql';

const DELETE_PROPOSAL_ATTACHMENT_MUTATION = graphql(`
  mutation DeleteProposalAttachment($input: DeleteProposalAttachmentInput!) {
    deleteProposalAttachment(input: $input)
  }
`);

/**
 * Detach a file from a Proposal. The file itself is deleted only if nothing
 * else still holds it — the same file may also be attached elsewhere, such as
 * to a Scheduled Event.
 */
const useDeleteProposalAttachment = (args?: {
  proposalId?: string;
  options?: UseMutationOptions<
    DeleteProposalAttachmentMutation,
    unknown,
    DeleteProposalAttachmentInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: DeleteProposalAttachmentInput) => {
      return gqlClient.request(DELETE_PROPOSAL_ATTACHMENT_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useDeleteProposalAttachment;
